import type { BadgeVariant } from '../../components/ui/badge';
import type { ReportingRun, ReportingSkipBreakdown } from '../../types/reporting';
import { runOutcome } from '../../lib/reporting/runOutcome';

// Pure view logic for the Sync Health page — run status derivation (incl. the
// partial-failure state the Cloud Run execution status hides), freshness level +
// relative-age formatting. All total + unit-tested; the page just renders these.

export type RunStatus = 'succeeded' | 'failed' | 'partial' | 'running' | 'died';

export interface RunStatusChip {
  label: string;
  variant: BadgeVariant;
}

// succeeded  - finished, zero failed accounts.
// partial    - finished; some accounts failed BUT some succeeded (the run mostly
//              worked; the failed_accounts list says which clubs to re-check).
// failed     - finished; accounts failed and NONE succeeded (a whole-run failure).
// running    - started, no finish yet, still inside the job's own task timeout.
// died       - started, no finish, and past that timeout: Cloud Run killed it.
//
// ⚠️⚠️ THE LAST TWO ARE NEW AND THE OLD FUNCTION COULD NOT SAY THEM.
// It read `failedCount`/`succeededCount` alone, which a start-only record leaves
// at zero - so a run killed mid-pull would have rendered "Succeeded 0 / 0 / 0", a
// corpse wearing a green chip. That is worse than the blank row it replaced,
// because a blank row at least looks like an absence.
//
// ⚠️ The running/died split DELEGATES to `runOutcome`, the same function the
// server reads for `diedRunCount`. Two implementations of "is this run dead" is
// exactly the defect class this round exists to remove, and they would drift at
// the first change to the task timeout.
export function runStatus(
  run: Pick<ReportingRun, 'failedCount' | 'succeededCount' | 'startedAt' | 'finishedAt'>,
  nowMs: number,
): RunStatus {
  if (run.finishedAt === null) {
    return runOutcome(run, nowMs) === 'died' ? 'died' : 'running';
  }
  if (run.failedCount === 0) return 'succeeded';
  if (run.succeededCount === 0) return 'failed';
  return 'partial';
}

const STATUS_CHIP: Record<RunStatus, RunStatusChip> = {
  succeeded: { label: 'Succeeded', variant: 'success' },
  partial: { label: 'Partial', variant: 'warning' },
  failed: { label: 'Failed', variant: 'danger' },
  // ⚠️ "Running" is NEUTRAL, not green: a run in flight has not succeeded
  // at anything yet, and a green chip on it would read as a result.
  running: { label: 'Running', variant: 'secondary' },
  // ⚠️ "Died" is RED and says what happened rather than naming a cause we
  // cannot see from here. Cloud Run kills a task for several reasons (memory,
  // timeout, a node going away) and the run record cannot tell them apart - the
  // Cloud Run execution log can. Claiming one would send someone to the wrong
  // place; the phrasing points at what IS known.
  died: { label: 'Died', variant: 'danger' },
};

export function runStatusChip(
  run: Pick<ReportingRun, 'failedCount' | 'succeededCount' | 'startedAt' | 'finishedAt'>,
  nowMs: number,
): RunStatusChip {
  return STATUS_CHIP[runStatus(run, nowMs)];
}

// ⚠️⚠️ AN UNFINISHED RUN HAS NO COUNTS, AND ZERO IS NOT THE ANSWER.
// A start record carries NULL counts on purpose - a run killed part-way got
// through an UNKNOWN number of accounts, not zero - but `toNum` coerces a NULL
// column to 0 at the read edge, so the wire object says `succeededCount: 0`. Left
// alone, the page prints "0 / 0 / 0" beside a "Died" chip, which reads as a run
// that achieved nothing: a measured failure we never observed (CLAUDE.md rule 15,
// at row scale).
//
// ⚠️ It keys on the FINISH, not on the outcome, so a RUNNING run dashes too -
// its counts are equally unwritten, and the distinction "in flight" vs "dead"
// belongs to the status chip rather than to the numbers beside it.
export function hasMeasuredCounts(run: Pick<ReportingRun, 'finishedAt'>): boolean {
  return run.finishedAt !== null;
}

// The freshness banner's health level, from hoursStale (null = no successful
// sync yet). Fresh < 3h; stale 3–12h; dead ≥ 12h (or never). The single "is the
// pipeline alive" signal — a scheduler misfire / IAM break / Zernio outage all
// surface as a growing hoursStale.
export type FreshnessLevel = 'fresh' | 'stale' | 'dead' | 'unknown';

export function freshnessLevel(hoursStale: number | null): FreshnessLevel {
  if (hoursStale === null) return 'unknown';
  if (hoursStale < 3) return 'fresh';
  if (hoursStale < 12) return 'stale';
  return 'dead';
}

// A run's wall-clock duration, e.g. "47s" / "4m 12s" / "6m". Returns "—" when
// either timestamp is null (a run still in flight) or the pair is invalid /
// out-of-order. Pure — derived from the stored started_at + finished_at.
export function formatRunDuration(startedAt: string | null, finishedAt: string | null): string {
  if (!startedAt || !finishedAt) return '—';
  const start = Date.parse(startedAt);
  const end = Date.parse(finishedAt);
  if (Number.isNaN(start) || Number.isNaN(end) || end < start) return '—';
  const totalSec = Math.round((end - start) / 1000);
  if (totalSec < 60) return `${totalSec}s`;
  const mins = Math.floor(totalSec / 60);
  const secs = totalSec % 60;
  return secs === 0 ? `${mins}m` : `${mins}m ${secs}s`;
}

// ---------------------------------------------------------------------------
// Skip breakdown — make the bare SKIP count legible (benign shared-dedup vs
// accounts that are sync-on but can't be pulled).
// ---------------------------------------------------------------------------

// Human labels for the SkipReason values (accountSelection.ts → SkipReason).
export const SKIP_REASON_LABELS: Record<string, string> = {
  'covered-by-shared-pull': 'shared ad account (pulled once)',
  'no-zernio-profile-link': 'no Zernio profile',
  'no-ad-account': 'no ad account',
  'profile-missing-from-cache': 'profile missing from cache',
  'not-in-requested-list': 'over the run limit',
  'requested-but-not-found': 'requested but not found',
};

// A reason's display label (falls back to the raw value for a future reason).
export function skipReasonLabel(reason: string): string {
  return SKIP_REASON_LABELS[reason] ?? reason;
}

// The `profile-missing-from-cache` skip is the concerning one — the account
// "thinks it's connected" but isn't being pulled. Flagged amber in the UI.
export function isConcerningSkipReason(reason: string): boolean {
  return reason === 'profile-missing-from-cache';
}

// Compact one-line breakdown, e.g. "57 shared-dedup · 11 not connected" (+ an
// "N other" segment for reasons in neither bucket, e.g. over-the-limit). Only
// non-zero segments render; an all-zero breakdown → "no skips".
export function formatSkipBreakdown(b: ReportingSkipBreakdown): string {
  const other = b.total - b.sharedDedup - b.notConnected;
  const segments: string[] = [];
  if (b.sharedDedup > 0) segments.push(`${b.sharedDedup} shared-dedup`);
  if (b.notConnected > 0) segments.push(`${b.notConnected} not connected`);
  if (other > 0) segments.push(`${other} other`);
  return segments.length > 0 ? segments.join(' · ') : 'no skips';
}

// Does this run have a skip worth a look (any "not connected" reason)? False for
// a legacy row (null breakdown) and for a benign shared-dedup-only run.
export function hasActionableSkips(run: Pick<ReportingRun, 'skipBreakdown'>): boolean {
  return run.skipBreakdown !== null && run.skipBreakdown.notConnected > 0;
}

// A compact relative age, e.g. "just now" / "47 min ago" / "3 h ago" / "2 d ago".
// Pure — `nowMs` is passed in. Returns null for a null/invalid timestamp.
export function formatRelativeAge(iso: string | null, nowMs: number): string | null {
  if (!iso) return null;
  const then = Date.parse(iso);
  if (Number.isNaN(then)) return null;
  const mins = Math.max(0, Math.round((nowMs - then) / 60_000));
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours} h ago`;
  const days = Math.round(hours / 24);
  return `${days} d ago`;
}
